"use client";

import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import Image from "next/image";
import AnimatedBorder from "@/components/ui/animated-border";

type CaseStudyVideoProps = {
  src: string;
  poster: string;
  posterMobile?: string;
  alt: string;
  width: number;
  height: number;
  durationSeconds?: number;
};

const CaseStudyVideo = ({
  src,
  poster,
  posterMobile,
  alt,
  width,
  height,
  durationSeconds = 90,
}: CaseStudyVideoProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const offsetRef = useRef(0);

  const [shouldLoad, setShouldLoad] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const getMaxScroll = useCallback(() => {
    const container = containerRef.current;
    const scroll = scrollRef.current;
    if (!container || !scroll) return 0;
    return Math.max(0, scroll.scrollHeight - container.clientHeight);
  }, []);

  const animate = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      const elapsed = (timestamp - startTimeRef.current) / 1000;
      const maxScroll = getMaxScroll();
      if (maxScroll <= 0) return;

      const currentOffset = offsetRef.current + (elapsed / durationSeconds) * maxScroll;

      if (currentOffset >= maxScroll) {
        offsetRef.current = maxScroll;
        setProgress(100);
        setIsPlaying(false);
        return;
      }

      if (scrollRef.current) {
        scrollRef.current.style.transform = `translateY(-${currentOffset}px)`;
      }
      setProgress((currentOffset / maxScroll) * 100);

      rafRef.current = requestAnimationFrame(animate);
    },
    [durationSeconds, getMaxScroll]
  );

  const play = useCallback(() => {
    startTimeRef.current = null;
    setIsPlaying(true);
    rafRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const pause = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const maxScroll = getMaxScroll();
    if (maxScroll > 0) {
      offsetRef.current = (progress / 100) * maxScroll;
    }
    setIsPlaying(false);
  }, [progress, getMaxScroll]);

  const handlePlay = useCallback(() => {
    if (!shouldLoad) {
      // First play — trigger image load, play starts in onLoad
      setShouldLoad(true);
      return;
    }
    if (isPlaying) {
      pause();
    } else {
      if (progress >= 99) {
        offsetRef.current = 0;
        setProgress(0);
        if (scrollRef.current) {
          scrollRef.current.style.transform = "translateY(0)";
        }
      }
      play();
    }
  }, [shouldLoad, isPlaying, play, pause, progress]);

  // Auto-play once the full image loads
  const handleImageLoaded = useCallback(() => {
    setIsLoaded(true);
    // Small delay to let the browser finish rendering
    requestAnimationFrame(() => {
      startTimeRef.current = null;
      setIsPlaying(true);
      rafRef.current = requestAnimationFrame(animate);
    });
  }, [animate]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <figure className="relative overflow-hidden rounded-[16px] md:rounded-[25px] bg-[#0a0619]">
      <div
        ref={containerRef}
        className="relative overflow-hidden h-[400px] md:h-[520px] lg:h-[597px]"
      >
        {/* Logo watermark — behind everything */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <Image
            src="/assets/brand/logo.svg"
            alt="Flyingcode logo"
            aria-hidden="true"
            width={166}
            height={106}
            className="w-[80px] md:w-[120px] lg:w-[140px] h-auto opacity-[0.07]"
          />
        </div>

        {/* Poster image — shown until full image loads */}
        {!isLoaded && (
          <div className="absolute inset-0 z-[1]">
            {posterMobile && (
              <Image
                src={posterMobile}
                alt={alt}
                width={1200}
                height={1200}
                sizes="100vw"
                className="absolute inset-0 w-full h-full object-cover object-top md:hidden"
                priority={false}
              />
            )}
            <Image
              src={poster}
              alt={alt}
              width={2432}
              height={1350}
              sizes="(min-width: 1280px) 1200px, 100vw"
              className={`absolute inset-0 w-full h-full object-cover object-top ${posterMobile ? "hidden md:block" : ""}`}
              priority={false}
            />
          </div>
        )}

        {/* The tall scrolling image — only loads after user hits play */}
        {shouldLoad && (
          <div
            ref={scrollRef}
            className="w-full will-change-transform relative z-[2]"
            style={{ transform: "translateY(0)" }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={alt}
              width={width}
              height={height}
              decoding="async"
              className="w-full h-auto"
              onLoad={handleImageLoaded}
            />
          </div>
        )}

        {/* Play overlay */}
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center z-10 cursor-pointer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={handlePlay}
            >
              {isLoaded && <div className="absolute inset-0 bg-[#0a0619]/60" />}
              <AnimatedBorder
                borderRadius="rounded-full"
                borderWidth={2}
                borderColor="#A855F7"
                background="#1B113B"
                hoverBackground="#1B113B"
                speed={3}
                spread={90}
                cut={3}
                className="shadow-[0_8px_40px_rgba(135,82,250,0.15)] relative z-10"
              >
                <button
                  className="w-[56px] h-[56px] md:w-[72px] md:h-[72px] flex items-center justify-center cursor-pointer"
                  aria-label={shouldLoad && !isLoaded ? "Loading..." : progress >= 99 ? "Replay" : "Play preview"}
                >
                  {shouldLoad && !isLoaded ? (
                    <motion.div
                      className="w-[20px] h-[20px] md:w-[24px] md:h-[24px] border-2 border-white/30 border-t-white rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    />
                  ) : progress >= 99 ? (
                    <svg className="w-[20px] h-[20px] md:w-[24px] md:h-[24px] text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 4v6h6" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                  ) : (
                    <svg className="w-[20px] h-[20px] md:w-[24px] md:h-[24px] text-white ml-[2px]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28a1 1 0 0 0-1.5.86z" />
                    </svg>
                  )}
                </button>
              </AnimatedBorder>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom controls — only after loaded */}
      {isLoaded && (
        <div className="px-[12px] md:px-[20px] py-[10px] md:py-[14px] flex items-center gap-[12px] bg-[#0a0619]">
          <button
            className="w-[32px] h-[32px] md:w-[36px] md:h-[36px] rounded-full bg-white/8 border border-white/10 flex items-center justify-center hover:bg-white/15 transition-colors duration-200 cursor-pointer shrink-0"
            onClick={handlePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg className="w-[11px] h-[11px] text-white" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg className="w-[11px] h-[11px] text-white ml-[1px]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28a1 1 0 0 0-1.5.86z" />
              </svg>
            )}
          </button>

          <div className="flex-1 h-[4px] bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-theme rounded-full transition-[width] duration-150 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </figure>
  );
};

export default CaseStudyVideo;
